import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { X, Search, CheckCircle, XCircle, Clock, FileText, Eye, ExternalLink, UserPlus, Users, FileCheck2, ShieldCheck, PartyPopper } from 'lucide-react';
import toast from 'react-hot-toast';
import { StatTile } from '../components/StatTile';
import { EmailSendModal } from '@/shared/components/ui/EmailSendModal';

const AVATAR_COLORS = [
  'bg-[#8B5CF6] text-white',
  'bg-[#10B981] text-white',
  'bg-[#F59E0B] text-white',
  'bg-[#3B82F6] text-white',
  'bg-[#EC4899] text-white',
  'bg-[#6366F1] text-white',
];

function getInitials(firstName?: string, lastName?: string) {
  const f = (firstName || '').trim();
  const l = (lastName || '').trim();
  if (f && l) return (f[0] + l[0]).toUpperCase();
  if (f) return f.slice(0, 2).toUpperCase();
  return '??';
}

function getAvatarColor(key: string) {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = key.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

// ── Types ────────────────────────────────────────────────────────────────────

interface CandidateDoc { code: string; label: string; required: boolean; applicableForFreshers: boolean; status: string; fileUrl: string; hasFile?: boolean; submittedAt?: string; verifiedBy?: any; rejectionReason?: string }
interface CompanyDoc { code: string; label: string; status: string; fileUrl: string; generatedAt?: string; acknowledgedAt?: string }
interface Candidate {
  _id: string; candidateId: string; employeeId?: string; source: string;
  firstName: string; lastName: string; email: string; phone: string; alternatePhone: string;
  dateOfBirth: string; gender: string; maritalStatus: string; bloodGroup: string; disability: string;
  aadharNumber: string; panNumber: string; uanNumber: string; esicNumber: string; drivingLicense: string;
  currentAddress: { street: string; city: string; state: string; pincode: string };
  permanentAddress: { street: string; city: string; state: string; pincode: string };
  bankName: string; accountNumber: string; ifscCode: string; branchName: string;
  family: { name: string; relation: string; contact: string }[];
  education: { schoolName: string; degree: string; fieldOfStudy: string; completionDate: string }[];
  experience: { company: string; occupation: string; duration: string; reasonForLeaving: string }[];
  designation: string; department: string; dateOfJoining: string; location: string;
  highestQualification: string; currentSalary: number; reportingTo: string;
  employmentType: string; isFresher: boolean; skillSet: string[];
  onboardingStatus: string; completionPercent: number;
  candidateDocuments: CandidateDoc[]; companyDocuments: CompanyDoc[];
  account?: { userId: string; username: string; active: boolean } | null;
  eJoiningToken: string; addedBy?: any; addedAt: string; modifiedBy?: any; modifiedAt?: string;
}

// ── Hooks ────────────────────────────────────────────────────────────────────

function useCandidates(filters: { page?: number; search?: string; status?: string; source?: string }) {
  return useQuery({
    queryKey: ['onboarding-candidates', filters],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (filters.page) p.append('page', String(filters.page));
      if (filters.search) p.append('search', filters.search);
      if (filters.status) p.append('status', filters.status);
      if (filters.source) p.append('source', filters.source);
      const { data } = await api.get(`/api/hr-onboarding-candidates/candidates?${p}`);
      return data as { data: Candidate[]; pagination: { total: number; page: number; totalPages: number } };
    },
  });
}

/**
 * Stage-count summary for the header stat row. Reuses the same
 * candidates endpoint the page's own list already calls (no new API),
 * just with a wider page so status breakdowns aren't skewed by pagination —
 * the same approach the HR Dashboard's Onboarding Pipeline widget uses.
 */
function useOnboardingStats() {
  return useQuery({
    queryKey: ['onboarding-candidates', 'stats'],
    queryFn: async () => {
      const { data } = await api.get('/api/hr-onboarding-candidates/candidates?limit=200');
      const list = (data?.data || []) as Candidate[];
      let inProgress = 0;
      let verification = 0;
      let completed = 0;
      for (const c of list) {
        if (c.onboardingStatus === 'completed') completed++;
        else if (c.onboardingStatus === 'verification') verification++;
        else inProgress++;
      }
      return { total: list.length, inProgress, verification, completed };
    },
    staleTime: 30_000,
  });
}

function useCandidateDetail(id: string | null) {
  return useQuery({
    queryKey: ['onboarding-candidates', 'detail', id],
    queryFn: async () => { const { data } = await api.get(`/api/hr-onboarding-candidates/candidates/${id}`); return data as Candidate; },
    enabled: !!id,
  });
}

function useAddDirectHire() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => { const { data } = await api.post('/api/hr-onboarding-candidates/candidates', payload); return data; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['onboarding-candidates'] }); },
  });
}

function useVerifyDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, code }: { id: string; code: string }) => { const { data } = await api.post(`/api/hr-onboarding-candidates/candidates/${id}/verify-doc`, { code }); return data; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['onboarding-candidates'] }); },
  });
}

function useRejectDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, code, reason }: { id: string; code: string; reason?: string }) => { const { data } = await api.post(`/api/hr-onboarding-candidates/candidates/${id}/reject-doc`, { code, reason }); return data; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['onboarding-candidates'] }); },
  });
}

interface OnboardingAccess { canRead: boolean; canManage: boolean; canConvert: boolean }

/** What the signed-in person may do with joiners (decided by the server from their HR grants). */
function useOnboardingAccess() {
  return useQuery({
    queryKey: ['onboarding-candidates', 'access'],
    queryFn: async () => (await api.get<OnboardingAccess>('/api/hr-onboarding-candidates/access')).data,
    staleTime: 60_000,
  });
}

function useRenewLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.post<{ eJoiningLink: string; expiresAt: string }>(`/api/hr-onboarding-candidates/candidates/${id}/regenerate-ejoining-link`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['onboarding-candidates'] }); },
  });
}

/** What the joiner uploaded needs the login token, so it is fetched and shown from a blob. */
async function openJoinerDocument(id: string, code: string) {
  const { data } = await api.get<Blob>(`/api/hr-onboarding-candidates/candidates/${id}/candidate-documents/${code}/file`, { responseType: 'blob' });
  const url = URL.createObjectURL(data);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Creating the account is refused when required documents are unverified; the server names them so HR can decide. */
function useConvertCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, allowIncompleteDocuments }: { id: string; allowIncompleteDocuments?: boolean }) =>
      (await api.post(`/api/hr-onboarding-candidates/candidates/${id}/convert-to-employee`, { allowIncompleteDocuments })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['onboarding-candidates'] }); },
  });
}

function useActivateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<{ account: { username: string }; emailDraft: { to: string; subject: string; html: string } }>(`/api/hr-onboarding-candidates/candidates/${id}/activate-account`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['onboarding-candidates'] }); },
  });
}

// ── Status helpers ───────────────────────────────────────────────────────────

const statusColors: Record<string, string> = {
  pending_info: 'bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A]',
  info_submitted: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE]',
  docs_pending: 'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA]',
  docs_submitted: 'bg-[#EDE9FE] text-[#6825F5] border border-purple-200',
  verification: 'bg-[#EDE9FE] text-[#7C3AED] border border-purple-200',
  completed: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0]',
};

const sourceLabels: Record<string, string> = { recruitment: 'Recruitment', website: 'Website', linkedin: 'LinkedIn', naukri: 'Naukri', direct_hire: 'Direct Hire' };

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export function HrOnboardingPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const { data, isLoading } = useCandidates({ page, search: search || undefined, status: statusFilter || undefined, source: sourceFilter || undefined });
  const candidates = data?.data || [];
  const pagination = data?.pagination;
  const { data: stats } = useOnboardingStats();
  const { data: access } = useOnboardingAccess();

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto pr-1">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200">
            <Users className="h-6 w-6 text-white" />
          </div>
          <div>
            <span className="block text-[11px] font-bold tracking-wider text-[#6825F5] uppercase">
              ONBOARDING
            </span>
            <h1 className="text-xl md:text-2xl font-bold tracking-tight text-[#11183F]">
              Onboarding Candidates &amp; Joiners
            </h1>
          </div>
        </div>
        {access?.canManage && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 rounded-full bg-[#6825F5] px-5 py-2 text-xs font-semibold text-white shadow-sm shadow-purple-200 transition-all hover:bg-[#581cd6] cursor-pointer self-start sm:self-auto"
          >
            <UserPlus className="h-4 w-4" />
            <span>Add Direct Hire</span>
          </button>
        )}
      </div>

      {/* Summary stats */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatTile
            icon={Users}
            label="In Pipeline"
            value={pagination?.total ?? stats.total}
            tone="primary"
            index={0}
          />
          <StatTile
            icon={FileCheck2}
            label="Info / Docs In Progress"
            value={stats.inProgress}
            tone="info"
            sublabel="Pending submissions"
            index={1}
          />
          <StatTile
            icon={ShieldCheck}
            label="In Verification"
            value={stats.verification}
            tone="warning"
            sublabel="Requires review"
            index={2}
          />
          <StatTile
            icon={PartyPopper}
            label="Completed"
            value={stats.completed}
            tone="success"
            sublabel="Converted to staff"
            index={3}
          />
        </div>
      )}

      {/* Main Table Card */}
      <Card className="rounded-2xl border border-[#E8E8F5] bg-white p-6 shadow-sm flex flex-col gap-4">
        {/* Filters */}
        <div className="flex gap-3 flex-wrap items-center justify-between">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3.5 top-3 h-4 w-4 text-[#94A3B8]" />
            <input
              className="w-full pl-10 pr-4 py-2 border border-[#E8E8F5] rounded-xl text-xs bg-white text-[#11183F] placeholder-[#94A3B8] focus:border-[#6825F5] focus:outline-none focus:ring-1 focus:ring-[#6825F5] transition"
              placeholder="Search by name, email, ID..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              className="border border-[#E8E8F5] rounded-xl px-4 py-2 text-xs font-semibold text-[#475569] bg-white shadow-xs focus:border-[#6825F5] focus:outline-none cursor-pointer"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Statuses</option>
              <option value="pending_info">Pending Info</option>
              <option value="info_submitted">Info Submitted</option>
              <option value="docs_submitted">Docs Submitted</option>
              <option value="verification">Verification</option>
              <option value="completed">Completed</option>
            </select>
            <select
              className="border border-[#E8E8F5] rounded-xl px-4 py-2 text-xs font-semibold text-[#475569] bg-white shadow-xs focus:border-[#6825F5] focus:outline-none cursor-pointer"
              value={sourceFilter}
              onChange={(e) => {
                setSourceFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Sources</option>
              <option value="recruitment">Recruitment</option>
              <option value="website">Website</option>
              <option value="linkedin">LinkedIn</option>
              <option value="naukri">Naukri</option>
              <option value="direct_hire">Direct Hire</option>
            </select>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="p-6">
            <Skeleton className="h-[400px]" />
          </div>
        ) : (
          <div className="rounded-2xl overflow-hidden border border-[#F1F3F9]">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-[#F8F9FE] text-[#747C9C] font-bold text-[11px] tracking-wider uppercase border-b border-[#F1F3F9]">
                  <tr>
                    <th className="px-5 py-3.5">Candidate ID</th>
                    <th className="px-4 py-3.5">Employee ID</th>
                    <th className="px-5 py-3.5">Name</th>
                    <th className="px-4 py-3.5">Email</th>
                    <th className="px-4 py-3.5">Department</th>
                    <th className="px-4 py-3.5">Status</th>
                    <th className="px-4 py-3.5">Source</th>
                    <th className="px-5 py-3.5 text-right">Completion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F3F9]">
                  {candidates.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-sm font-medium text-[#747C9C]">
                        No candidates found.
                      </td>
                    </tr>
                  )}
                  {candidates.map((c) => {
                    const initials = getInitials(c.firstName, c.lastName);
                    const colorClass = getAvatarColor(c._id || c.candidateId);
                    return (
                      <tr
                        key={c._id}
                        onClick={() => setSelectedId(c._id)}
                        className={`cursor-pointer hover:bg-[#F8F9FE]/70 transition-colors ${selectedId === c._id ? 'bg-[#EDE9FE]/40' : ''}`}
                      >
                        <td className="px-5 py-3.5 font-mono text-xs font-semibold text-[#6825F5]">
                          <span className="bg-[#EDE9FE]/70 px-2.5 py-1 rounded-md inline-block">
                            {c.candidateId}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 font-mono text-xs">
                          {c.employeeId ? (
                            <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-0.5 border border-indigo-200 font-semibold text-[#6825F5]">
                              {c.employeeId}
                            </span>
                          ) : (
                            <span className="text-[#94A3B8]">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 font-medium">
                          <div className="flex items-center gap-3">
                            <div
                              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${colorClass}`}
                            >
                              {initials}
                            </div>
                            <span className="text-sm font-semibold text-[#11183F]">
                              {c.firstName} {c.lastName}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-xs text-[#64748B] truncate max-w-[160px]">
                          {c.email || '—'}
                        </td>
                        <td className="px-4 py-3.5 text-xs font-medium text-[#475569] capitalize">
                          {c.department || '—'}
                        </td>
                        <td className="px-4 py-3.5">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold capitalize ${statusColors[c.onboardingStatus] || 'bg-gray-100 text-gray-700'}`}>
                            {c.onboardingStatus?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-xs text-[#475569]">
                          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 font-medium text-slate-700 text-[11px]">
                            {sourceLabels[c.source] || c.source}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-2.5">
                            <div className="w-16 h-2 rounded-full bg-slate-100 overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-[#6825F5] to-[#8B5CF6] rounded-full transition-all duration-300"
                                style={{ width: `${c.completionPercent}%` }}
                              />
                            </div>
                            <span className="text-xs font-bold text-[#11183F] min-w-[28px]">
                              {c.completionPercent}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="pt-3 border-t border-[#F1F3F9] flex items-center justify-between text-xs">
            <span className="text-[#747C9C] font-medium">
              Total Record Count: <strong className="text-[#6825F5] font-bold">{pagination.total}</strong>
            </span>
            <div className="flex items-center gap-1.5">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1 border border-[#E8E8F5] rounded-lg disabled:opacity-30 hover:bg-[#F8F9FE] transition cursor-pointer"
              >
                &lt;
              </button>
              <span className="px-3 py-1 font-semibold text-[#11183F]">
                {page} / {pagination.totalPages}
              </span>
              <button
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 border border-[#E8E8F5] rounded-lg disabled:opacity-30 hover:bg-[#F8F9FE] transition cursor-pointer"
              >
                &gt;
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Right: Detail Modal */}
      {selectedId && <CandidateDetailModal candidateId={selectedId} onClose={() => setSelectedId(null)} />}

      {/* Add Direct Hire Modal */}
      <AddDirectHireModal open={showAdd} onClose={() => setShowAdd(false)} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CANDIDATE DETAIL DRAWER
// ═══════════════════════════════════════════════════════════════════════════════

function CandidateDetailModal({ candidateId, onClose }: { candidateId: string; onClose: () => void }) {
  const navigate = useNavigate();
  const { data: c, isLoading } = useCandidateDetail(candidateId);
  const verifyMut = useVerifyDoc();
  const rejectMut = useRejectDoc();
  const renewMut = useRenewLink();
  const convertMut = useConvertCandidate();
  const activateMut = useActivateAccount();
  const [activationDraft, setActivationDraft] = useState<{ to: string; subject: string; html: string } | null>(null);
  const { data: access } = useOnboardingAccess();
  const canManage = access?.canManage === true;
  const canConvert = access?.canConvert === true;

  const convert = async (id: string, allowIncompleteDocuments = false) => {
    try {
      const res = await convertMut.mutateAsync({ id, allowIncompleteDocuments });
      toast.success(`Account created for ${res.account.username}. It stays switched off until you activate it.`);
    } catch (err: any) {
      const data = err?.response?.data;
      if (err?.response?.status === 409 && data?.pendingDocuments?.length && !allowIncompleteDocuments) {
        const names = data.pendingDocuments.map((d: { label: string }) => d.label).join(', ');
        if (window.confirm(`These required documents are not verified yet:\n\n${names}\n\nCreate the account anyway?`)) await convert(id, true);
        return;
      }
      toast.error(data?.error || 'Could not create the account.');
    }
  };

  const activate = async (id: string) => {
    if (!window.confirm('Switch the login on now? A temporary password is made and shown once in an e-mail draft.')) return;
    try {
      const res = await activateMut.mutateAsync(id);
      toast.success(`Login switched on for ${res.account.username}.`);
      setActivationDraft(res.emailDraft);
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Could not activate the account.'); }
  };

  if (isLoading || !c) return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{backgroundColor:'rgba(0,0,0,0.6)'}}>
      <div className="bg-white rounded-xl w-[90vw] max-w-5xl h-[85vh] p-6"><Skeleton className="h-full" /></div>
    </div>
  );

  const docIcon = (s: string) => {
    if (s === 'verified') return <CheckCircle className="h-4 w-4 text-green-500" />;
    if (s === 'rejected') return <XCircle className="h-4 w-4 text-red-500" />;
    if (s === 'submitted') return <FileText className="h-4 w-4 text-blue-500" />;
    if (s === 'not_applicable') return <span className="text-[10px] text-gray-400">N/A</span>;
    return <Clock className="h-4 w-4 text-gray-400" />;
  };

  return (
    <>
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{backgroundColor:'rgba(0,0,0,0.6)'}} onClick={onClose}>
      <div className="bg-white rounded-xl w-[92vw] max-w-6xl h-[88vh] flex flex-col overflow-hidden shadow-2xl border" onClick={e => e.stopPropagation()}>
      {/* Modal Header */}
      <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50 rounded-t-xl shrink-0">
        <div>
          <h2 className="font-semibold text-lg text-gray-900">{c.firstName} {c.lastName}</h2>
          <p className="text-xs text-gray-500">{c.candidateId}{c.employeeId ? ` · ${c.employeeId}` : ''} — {c.designation || 'Position TBD'} — <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${statusColors[c.onboardingStatus]}`}>{c.onboardingStatus?.replace(/_/g, ' ')}</span></p>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg transition"><X className="h-5 w-5 text-gray-600" /></button>
      </div>

      {/* Scrollable content - 2 column layout for wider modal */}
      <div className="flex-1 overflow-auto p-6 bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 text-sm">

        {/* Section: Candidate Details */}
        <Section title="Candidate Details">
          <Grid>
            <KV label="Candidate ID" value={c.candidateId} />
            <KV label="Employee ID" value={c.employeeId || '-'} />
            <KV label="First Name" value={c.firstName} />
            <KV label="Last Name" value={c.lastName} />
            <KV label="Email ID" value={c.email} />
            <KV label="Phone" value={c.phone} />
            <KV label="Alternate Phone" value={c.alternatePhone} />
            <KV label="Date of Birth" value={c.dateOfBirth?.slice(0, 10)} />
            <KV label="Gender" value={c.gender} />
            <KV label="Marital Status" value={c.maritalStatus} />
            <KV label="Blood Group" value={c.bloodGroup} />
            <KV label="Disability" value={c.disability} />
            <KV label="UAN Number" value={c.uanNumber} />
            <KV label="Aadhar Number" value={c.aadharNumber} />
            <KV label="PAN Number" value={c.panNumber} />
          </Grid>
        </Section>

        {/* Section: Address */}
        <Section title="Address Details">
          <Grid>
            <KV label="Current Address" value={fmtAddr(c.currentAddress)} span />
            <KV label="Permanent Address" value={fmtAddr(c.permanentAddress)} span />
          </Grid>
        </Section>

        {/* Section: Bank */}
        <Section title="Bank Details">
          <Grid>
            <KV label="Bank Name" value={c.bankName} />
            <KV label="Account Number" value={c.accountNumber} />
            <KV label="IFSC Code" value={c.ifscCode} />
            <KV label="Branch" value={c.branchName} />
          </Grid>
        </Section>

        {/* Section: Family */}
        <Section title="Family Details">
          {c.family?.length ? (
            <table className="w-full text-xs border-collapse"><thead><tr className="bg-muted/50"><th className="px-2 py-1.5 text-left">S.No</th><th className="px-2 py-1.5 text-left">Name</th><th className="px-2 py-1.5 text-left">Relation</th><th className="px-2 py-1.5 text-left">Contact</th></tr></thead>
            <tbody>{c.family.map((f, i) => <tr key={i} className="border-t"><td className="px-2 py-1.5">{i+1}</td><td className="px-2 py-1.5">{f.name}</td><td className="px-2 py-1.5 capitalize">{f.relation}</td><td className="px-2 py-1.5">{f.contact}</td></tr>)}</tbody></table>
          ) : <p className="text-muted-foreground text-xs">No family details.</p>}
        </Section>

        {/* Section: Professional */}
        <Section title="Professional Details">
          <Grid>
            <KV label="Designation" value={c.designation} />
            <KV label="Department" value={c.department} />
            <KV label="Date of Joining" value={c.dateOfJoining?.slice(0, 10)} />
            <KV label="Location" value={c.location} />
            <KV label="Source of Hire" value={sourceLabels[c.source] || c.source} />
            <KV label="Skill Set" value={c.skillSet?.join(', ')} />
            <KV label="Highest Qualification" value={c.highestQualification} />
            <KV label="Current Salary" value={c.currentSalary ? `₹${c.currentSalary.toLocaleString('en-IN')}` : '-'} />
            <KV label="Reporting To" value={c.reportingTo} />
            <KV label="Employment Type" value={c.employmentType} />
            <KV label="Fresher" value={c.isFresher ? 'Yes' : 'No'} />
          </Grid>
        </Section>

        {/* Section: Education */}
        <Section title="Education">
          {c.education?.length ? (
            <table className="w-full text-xs border-collapse"><thead><tr className="bg-muted/50"><th className="px-2 py-1.5 text-left">School Name</th><th className="px-2 py-1.5 text-left">Degree</th><th className="px-2 py-1.5 text-left">Field</th><th className="px-2 py-1.5 text-left">Completion</th></tr></thead>
            <tbody>{c.education.map((e, i) => <tr key={i} className="border-t"><td className="px-2 py-1.5">{e.schoolName||'-'}</td><td className="px-2 py-1.5">{e.degree||'-'}</td><td className="px-2 py-1.5">{e.fieldOfStudy||'-'}</td><td className="px-2 py-1.5">{e.completionDate?.slice(0,10)||'-'}</td></tr>)}</tbody></table>
          ) : <p className="text-muted-foreground text-xs">No education records.</p>}
        </Section>

        {/* Section: Experience */}
        <Section title="Work Experience">
          {c.isFresher ? <p className="text-muted-foreground text-xs italic">Fresher — no previous experience.</p> : c.experience?.length ? (
            <table className="w-full text-xs border-collapse"><thead><tr className="bg-muted/50"><th className="px-2 py-1.5 text-left">Company</th><th className="px-2 py-1.5 text-left">Role</th><th className="px-2 py-1.5 text-left">Duration</th><th className="px-2 py-1.5 text-left">Reason</th></tr></thead>
            <tbody>{c.experience.map((e, i) => <tr key={i} className="border-t"><td className="px-2 py-1.5">{e.company||'-'}</td><td className="px-2 py-1.5">{e.occupation||'-'}</td><td className="px-2 py-1.5">{e.duration||'-'}</td><td className="px-2 py-1.5">{e.reasonForLeaving||'-'}</td></tr>)}</tbody></table>
          ) : <p className="text-muted-foreground text-xs">No experience records.</p>}
        </Section>

        {/* Section: Company Documents (Issued) — full width */}
        <div className="lg:col-span-2">
        <Section title="Company Documents">
          <div className="space-y-1.5">
            {c.companyDocuments?.map(doc => (
              <div key={doc.code} className="flex items-center justify-between p-2 border rounded">
                <div className="flex items-center gap-2">
                  {doc.status === 'issued' || doc.status === 'acknowledged' ? <CheckCircle className="h-3.5 w-3.5 text-green-500" /> : <Clock className="h-3.5 w-3.5 text-gray-400" />}
                  <span className="text-xs">{doc.label}</span>
                </div>
                <div className="flex gap-1">
                  {doc.code !== 'relieving_letter' && (
                    <button onClick={() => {
                      onClose();
                      navigate(`/app/hr/onboarding/${c._id}/documents/${doc.code}`);
                    }} className="text-[10px] px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 flex items-center gap-1"><Eye className="h-3 w-3" />{doc.status === 'not_generated' ? 'Preview' : doc.status === 'draft' ? 'Review draft' : 'View'}</button>
                  )}
                  {doc.status === 'issued' && <span className="text-[10px] text-green-600 font-medium">Issued ✓</span>}
                </div>
              </div>
            ))}
          </div>
        </Section>
        </div>

        {/* Section: Candidate Documents (Uploaded) — full width */}
        <div className="lg:col-span-2">
        <Section title="Candidate Documents">
          <div className="space-y-1.5">
            {c.candidateDocuments?.map(doc => (
              <div key={doc.code} className="flex items-center justify-between p-2 border rounded">
                <div className="flex items-center gap-2">
                  {docIcon(doc.status)}
                  <div>
                    <span className="text-xs">{doc.label}</span>
                    {doc.required && doc.status !== 'not_applicable' && <span className="text-red-500 text-[10px] ml-1">*</span>}
                    {doc.status === 'rejected' && <span className="text-[10px] text-red-500 ml-2">({doc.rejectionReason})</span>}
                  </div>
                </div>
                <div className="flex gap-1">
                  {doc.hasFile && canManage && (doc.status === 'submitted' || doc.status === 'verified') && (
                    <button onClick={() => openJoinerDocument(c._id, doc.code).catch(() => toast.error('Could not open the file.'))} className="text-[10px] px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 flex items-center gap-1"><Eye className="h-3 w-3" />View file</button>
                  )}
                  {doc.status === 'submitted' && canManage && <>
                    <button onClick={() => verifyMut.mutateAsync({ id: c._id, code: doc.code }).then(() => toast.success('Verified.'))} className="text-[10px] px-2 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100">Verify</button>
                    <button onClick={() => { const r = prompt('Rejection reason:'); if (r) rejectMut.mutateAsync({ id: c._id, code: doc.code, reason: r }).then(() => toast.success('Rejected.')); }} className="text-[10px] px-2 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100">Reject</button>
                  </>}
                  {doc.status === 'verified' && <span className="text-[10px] text-green-600 font-medium">Verified ✓</span>}
                  {doc.status === 'not_applicable' && <span className="text-[10px] text-gray-400 italic">N/A</span>}
                  {doc.status === 'pending' && <span className="text-[10px] text-orange-500">Awaiting</span>}
                </div>
              </div>
            ))}
          </div>
        </Section>
        </div>

        {/* Section: Onboarding Meta */}
        <Section title="Onboarding Info">
          {canConvert && (
            <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 p-3 text-xs">
              {c.onboardingStatus !== 'completed' && (
                <>
                  <span className="text-muted-foreground">Ready to join? Create the employee account (switched off until you activate it).</span>
                  <Button size="sm" disabled={convertMut.isPending} onClick={() => convert(c._id)}>Create employee account</Button>
                </>
              )}
              {c.onboardingStatus === 'completed' && c.account && !c.account.active && (
                <>
                  <span className="text-muted-foreground">Account <strong>{c.account.username}</strong> is created but switched off.</span>
                  <Button size="sm" disabled={activateMut.isPending} onClick={() => activate(c._id)}>Activate login</Button>
                </>
              )}
              {c.onboardingStatus === 'completed' && c.account?.active && (
                <span className="text-green-700">Account <strong>{c.account.username}</strong> is active.</span>
              )}
            </div>
          )}
          <Grid>
            <KV label="Status" value={c.onboardingStatus?.replace(/_/g, ' ')} />
            <KV label="Completion" value={`${c.completionPercent}%`} />
            <KV label="Added By" value={c.addedBy?.display_name || '-'} />
            <KV label="Added Time" value={c.addedAt ? new Date(c.addedAt).toLocaleString('en-IN') : '-'} />
            <KV label="Modified By" value={c.modifiedBy?.display_name || '-'} />
            <KV label="Modified Time" value={c.modifiedAt ? new Date(c.modifiedAt).toLocaleString('en-IN') : '-'} />
            <KV label="E-Joining Link" value={c.eJoiningToken ? `${window.location.origin}/e-joining/${c.eJoiningToken}` : 'Not available (expired or no access)'} span link />
            {canManage && c.onboardingStatus !== 'completed' && (
              <div className="col-span-2 flex gap-2">
                {c.eJoiningToken && (
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/e-joining/${c.eJoiningToken}`); toast.success('Link copied.'); }}>Copy link</Button>
                )}
                <Button size="sm" variant="outline" disabled={renewMut.isPending} onClick={() => renewMut.mutateAsync(c._id).then(() => toast.success('New link created. The old one no longer works.')).catch((err: any) => toast.error(err?.response?.data?.error || 'Failed.'))}>
                  Renew link (14 days)
                </Button>
              </div>
            )}
          </Grid>
        </Section>
      </div>
      </div>
      </div>
    </div>
    {activationDraft && (
      <EmailSendModal
        open={!!activationDraft}
        onClose={() => setActivationDraft(null)}
        initialTo={activationDraft.to}
        initialSubject={activationDraft.subject}
        initialHtml={activationDraft.html}
        onSent={() => setActivationDraft(null)}
      />
    )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ADD DIRECT HIRE MODAL
// ═══════════════════════════════════════════════════════════════════════════════

function AddDirectHireModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const addMut = useAddDirectHire();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', employeeId: '', designation: '', department: '', dateOfJoining: '', location: '', isFresher: false });
  const set = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }));

  const handleCreate = async () => {
    if (!form.firstName || !form.lastName) { toast.error('First and last name required.'); return; }
    try {
      const result = await addMut.mutateAsync(form);
      toast.success(`Direct hire added. E-Joining link: ${window.location.origin}${result.eJoiningLink}`, { duration: 10000 });
      if (result.potentialDuplicates?.length) toast(`Possible duplicate: ${result.potentialDuplicates.length} existing joiner(s) share this email or phone.`, { icon: '⚠️', duration: 8000 });
      onClose();
      setForm({ firstName: '', lastName: '', email: '', phone: '', employeeId: '', designation: '', department: '', dateOfJoining: '', location: '', isFresher: false });
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Failed.'); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Direct Hire" size="lg" footer={
      <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={handleCreate} disabled={addMut.isPending}>{addMut.isPending ? 'Adding...' : 'Add & Generate E-Joining Link'}</Button></div>
    }>
      <div className="space-y-3 py-3">
        <p className="text-sm text-muted-foreground">Add a candidate who was hired outside the recruitment pipeline (referral, direct hire). An E-Joining link will be generated for them to fill their details.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input name="dh-fn" label="First Name *" value={form.firstName} onChange={e => set('firstName', e.target.value)} />
          <Input name="dh-ln" label="Last Name *" value={form.lastName} onChange={e => set('lastName', e.target.value)} />
          <Input name="dh-empid" label="Employee ID" value={form.employeeId} onChange={e => set('employeeId', e.target.value)} placeholder="e.g. EMP - 011 (Auto-generated if blank)" />
          <Input name="dh-email" label="Email" value={form.email} onChange={e => set('email', e.target.value)} />
          <Input name="dh-phone" label="Phone" value={form.phone} onChange={e => set('phone', e.target.value)} />
          <Input name="dh-desg" label="Designation" value={form.designation} onChange={e => set('designation', e.target.value)} placeholder="e.g. BDE" />
          <Input name="dh-dept" label="Department" value={form.department} onChange={e => set('department', e.target.value)} placeholder="e.g. Sales" />
          <Input name="dh-doj" label="Date of Joining" type="date" value={form.dateOfJoining} onChange={e => set('dateOfJoining', e.target.value)} />
          <Input name="dh-loc" label="Location" value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Pune" />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={form.isFresher} onChange={e => set('isFresher', e.target.checked)} className="rounded" />
          <label className="text-sm">Candidate is a fresher (no previous experience docs needed)</label>
        </div>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITY COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 pb-1 border-b">{title}</h3>
      {children}
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">{children}</div>;
}

function KV({ label, value, span, link }: { label: string; value?: string; span?: boolean; link?: boolean }) {
  return (
    <div className={span ? 'col-span-2' : ''}>
      <span className="text-[11px] text-muted-foreground">{label}</span>
      {link && value && value !== '-' ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="block text-xs font-medium text-primary hover:underline flex items-center gap-1">{value}<ExternalLink className="h-3 w-3" /></a>
      ) : (
        <p className="text-xs font-medium capitalize">{value || '-'}</p>
      )}
    </div>
  );
}

function fmtAddr(addr?: { street?: string; city?: string; state?: string; pincode?: string }) {
  if (!addr) return '-';
  return [addr.street, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ') || '-';
}
